import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  FormControlLabel,
  Switch,
  Grid,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../context";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useAdd from "../../../../../api/useAdd";
import { useQueryClient } from "@tanstack/react-query";

const XRayDialog = ({
  open,
  onClose,
  patientId,
  doctorId,
  ticketId = null,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const queryClient = useQueryClient();

  const { data: xrayTypesData } = useFetchObjects(
    ["xray-types"],
    "xray-types/",
    token,
  );

  const { handleAdd, loading, resetForm } = useAdd(
    "patient-xray",
    token,
    t("profile.xrayAdded") || "X-Ray order created successfully",
    t("profile.xrayAddFailed") || "Failed to create X-Ray order",
  );

  const [formData, setFormData] = useState({
    xray_type: null,
    date: new Date().toISOString().split("T")[0],
    doctor_notes: "",
    is_urgent: false,
    total_amount: 0,
  });
  const [errors, setErrors] = useState({});

  const xrayTypes = xrayTypesData?.results || xrayTypesData || [];

  useEffect(() => {
    if (resetForm) {
      setFormData({
        xray_type: null,
        date: new Date().toISOString().split("T")[0],
        doctor_notes: "",
        is_urgent: false,
        total_amount: 0,
      });
      setErrors({});
      onClose();
      queryClient.invalidateQueries(["unified-ehr", patientId]);
    }
  }, [resetForm, onClose, queryClient, patientId]);

  const handleXrayTypeChange = (event, newValue) => {
    setFormData({
      ...formData,
      xray_type: newValue?.id || null,
      total_amount: newValue?.cost || 0,
    });
    if (errors.xray_type) {
      setErrors({ ...errors, xray_type: "" });
    }
  };

  const handleSubmit = () => {
    const newErrors = {};
    if (!formData.xray_type) {
      newErrors.xray_type =
        t("profile.xrayTypeRequired") || "X-Ray type is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const submitData = {
      patient: patientId,
      assigned_doctor: doctorId,
      ticket: ticketId || null,
      xray_type: formData.xray_type,
      date: formData.date,
      doctor_notes: formData.doctor_notes,
      is_urgent: formData.is_urgent,
      total_amount: formData.total_amount,
    };

    handleAdd(submitData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("profile.addXRay") || "Add X-Ray Order"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Autocomplete
              options={xrayTypes}
              getOptionLabel={(option) => option.name || ""}
              value={xrayTypes.find((t) => t.id === formData.xray_type) || null}
              onChange={handleXrayTypeChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("profile.xrayType") || "X-Ray Type"}
                  error={!!errors.xray_type}
                  helperText={errors.xray_type}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("profile.date") || "Date"}
              type="date"
              value={formData.date}
              onChange={(e) =>
                setFormData({ ...formData, date: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("profile.totalAmount") || "Total Amount"}
              type="number"
              value={formData.total_amount}
              onChange={(e) =>
                setFormData({ ...formData, total_amount: e.target.value })
              }
              size="small"
              InputProps={{
                readOnly: formData.xray_type !== null,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_urgent}
                  onChange={(e) =>
                    setFormData({ ...formData, is_urgent: e.target.checked })
                  }
                />
              }
              label={t("profile.urgent") || "Urgent"}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("profile.notes") || "Notes"}
              multiline
              rows={3}
              value={formData.doctor_notes}
              onChange={(e) =>
                setFormData({ ...formData, doctor_notes: e.target.value })
              }
              size="small"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("profile.cancel") || "Cancel"}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading
            ? t("profile.saving") || "Saving..."
            : t("profile.add") || "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default XRayDialog;
